<script>
import ActivityDialog from '@/components/sites/ActivityDialog';
import ContentBox from '@/components/ui/ContentBox';
import ControlBar from '@/components/layouts/dashboard/ControlBar';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import DuplicateDialog from '@/components/pages/DuplicateDialog';
import EmptyState from '@/components/ui/EmptyState';
import EnablePagesDialog from '@/components/pages/EnablePagesDialog';
import FileBrowser from '@/components/file-browser/FileBrowser';
import PageCard from '@/components/pages/PageCard';
import RenameDialog from '@/components/pages/RenameDialog';
import Selectable from '@/components/selectable/Selectable';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import Promise from 'bluebird';
import debounce from 'lodash/debounce';
import getErrorMessage from '@/modules/server-errors';
import { extname } from '@/modules/file-utilities';
import { sourceOnlyExtensions } from '@/modules/editor/file-types';

export default {
  name: 'Site',
  components: {
    ActivityDialog,
    ContentBox,
    ControlBar,
    DashboardLayout,
    DuplicateDialog,
    EmptyState,
    EnablePagesDialog,
    FileBrowser,
    PageCard,
    RenameDialog,
    Selectable,
    SidebarLink,
    SidebarLinkGroup
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: '',
      breadcrumbs: [],
      canLoadMore: false,
      canShowPreview: false,
      fileBrowserDialogVisible: false,
      hasPages: false,
      hasTemplates: false,
      iframeSrc: 'about:blank',
      isDeleting: false,
      isPreviewLoading: false,
      isSearching: false,
      limit: 50,
      lastSelectedIndex: 0,
      offset: 0,
      pages: [],
      search: '',
      selection: [],
      site: {},
      statusCode: null
    };
  },
  watch: {
    selection() {
      // Can we show a preview?
      if (this.selection.length === 1) {
        // Cannot show a preview for source only files
        const extension = extname(this.selection[0].path);
        this.canShowPreview = !sourceOnlyExtensions.includes(extension);
      } else {
        // Can only show a preview for individual files
        this.canShowPreview = this.selection.length === 1;
      }

      // Update the iframe src
      if (this.selection.length === 1) {
        const page = this.selection[0];
        const newSrc = `/api/sites/${this.site.id}/pages/${page.id}/render`;
        if (this.iframeSrc !== newSrc) {
          this.isPreviewLoading = true;
          this.iframeSrc = `/api/sites/${this.site.id}/pages/${page.id}/render`;
        }
      } else {
        this.isPreviewLoading = false;
        this.iframeSrc = 'about:blank';
      }
    }
  },
  created() {
    // Fetch initial data
    Promise.all([
      this.$http.get(`/sites/${this.$route.params.siteId}`),
      this.$http.get(`/sites/${this.$route.params.siteId}/templates`, {
        params: { limit: 0 }
      }),
      this.fetchPages('', this.limit, 0)
    ])
      .then(res => {
        this.site = res[0].data.site;
        this.hasTemplates = res[1].data.count > 0;
        this.hasPages = res[2].data.count > 0;
        this.title = this.site.name;
        this.breadcrumbs = [{ text: this.$t('Sites'), to: { name: 'Sites' } }, { text: this.site.name }];
        this.statusCode = 200;

        this.$nextTick(() => {
          this.$refs.pageCards.$el.addEventListener('scroll', this.handleScroll);
          this.$refs.previewFrame.addEventListener('load', this.handlePreviewLoad);
          if (this.$refs.search) this.$refs.search.focus();
        });
      })
      .catch(err => (this.statusCode = err.response.status));
  },
  beforeRouteLeave(to, from, next) {
    if (this.$refs.pageCards) {
      this.$refs.pageCards.$el.removeEventListener('scroll', this.handleScroll);
    }

    if (this.$refs.previewFrame) {
      this.$refs.previewFrame.removeEventListener('load', this.handlePreviewLoad);
    }

    next();
  },
  methods: {
    // Fetches a list of pages
    fetchPages(search, limit, offset) {
      this.isSearching = true;
      return this.$http
        .get(`/sites/${this.$route.params.siteId}/pages`, {
          params: {
            search,
            limit,
            offset,
            sortBy: 'pinned'
          }
        })
        .then(res => {
          this.canLoadMore = res.data.count > res.data.offset + res.data.limit;
          this.numPages = res.data.count;
          this.offset = res.data.offset;

          if (res.data.offset === 0) {
            // Reset the page list
            this.setPages(res.data.pages);
          } else {
            // Append to the page list
            this.setPages(this.pages.concat(res.data.pages));
          }

          return res;
        })
        .finally(() => (this.isSearching = false));
    },

    // Gets a list of files
    getFiles(path) {
      return this.$http
        .get(`/sites/${this.site.id}/files`, {
          params: { path }
        })
        .then(res => res.data.files);
    },

    handleEnabledPage(page) {
      this.hasPages = true;
      this.setPages(this.pages.concat(page));
    },

    openFileBrowser() {
      this.fileBrowserDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.fileBrowser.open({ path: this.site.homeFolder });
      });
    },

    // Pins or unpins a list of pages
    pinPages(pages, isPinned) {
      // Update the UI first
      pages.map(page => (page.isPinned = isPinned));
      this.setPages(this.pages);

      // Update each page
      Promise.map(
        pages,
        page => {
          return this.$http.put(`/sites/${this.site.id}/pages/${page.id}`, { isPinned });
        },
        { concurrency: 3 }
      )
        .then(() => this.setPages(this.pages))
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        });
    },

    // Deletes one or more pages
    deletePage(pages, deleteFromServer) {
      const pageIds = pages.map(page => page.id);
      const action = deleteFromServer ? this.$t('Delete') : this.$t('Disable');
      const message = deleteFromServer
        ? this.$t('This will permanently delete the selected pages from your web server.')
        : this.$t('This will remove the selected pages from the CMS, but not from your web server.');

      this.$confirm(message, {
        title: action,
        confirmButtonText: action,
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('Cancel'),
        callback: action => {
          if (action === 'confirm') {
            this.isDeleting = true;
            this.$http
              .post(`/sites/${this.site.id}/pages/delete`, {
                pageIds,
                deleteFromServer
              })
              .then(res => {
                const succeeded = res.data.succeeded.map(page => page.id);

                // Remove the deleted pages
                this.setPages(this.pages.filter(page => !succeeded.includes(page.id)));

                // Update hasPages if no pages appear to be left
                if (this.pages.length === 0) {
                  this.$http
                    .get(`/sites/${this.site.id}/pages`, {
                      params: { limit: 0 }
                    })
                    .then(res => (this.hasPages = res.data.count > 0));
                }
              })
              .catch(err => {
                this.$notify({
                  type: 'error',
                  title: this.$t('Error'),
                  message: getErrorMessage(err.response.data.code)
                });
              })
              .finally(() => (this.isDeleting = false));
          }
        }
      });
    },

    // Duplicate a page
    pageDuplicated(page) {
      this.setPages(this.pages.concat(page));
      this.$refs.selectable.select(page);
    },

    // Rename a page
    pageRenamed(page) {
      this.pages = this.pages.filter(p => p.id !== page.id);
      this.setPages(this.pages.concat(page));
    },

    // Refreshes the list as the user types a search
    handleSearch: debounce(function () {
      this.fetchPages(this.search, this.limit, 0);
    }, 250),

    // Loads more when scrolling reaches the bottom
    handleScroll: debounce(
      function () {
        const el = this.$refs.pageCards.$el;
        const scrollTop = el.scrollTop;
        const scrollHeight = el.scrollHeight || el.scrollHeight;
        const elHeight = el.clientHeight;
        const threshold = elHeight * 4;

        if (scrollTop > scrollHeight - threshold) {
          this.loadMore();
        }
      },
      500,
      { maxWait: 100 }
    ),

    // Loads the next batch of pages
    loadMore() {
      if (this.canLoadMore) {
        this.fetchPages(this.search, this.limit, this.offset + this.limit);
      }
    },

    // Called when the iframe loads a preview
    handlePreviewLoad() {
      const iframe = this.$refs.previewFrame;
      const doc = iframe.contentWindow.document;
      const html = doc.querySelector('html');
      const body = doc.querySelector('body');
      this.isPreviewLoading = false;

      // Direct clicks to the editor
      if (html) {
        html.style.cursor = 'pointer';
        html.addEventListener('mousedown', event => event.preventDefault());
        html.addEventListener('click', event => {
          const siteId = this.site.id;
          const pageId = this.selection[0].id;
          event.preventDefault();
          this.$router.push({ name: 'EditPage', params: { siteId, pageId } });
        });
      }

      // Disable user interaction
      if (body) {
        body.style.pointerEvents = 'none';
        body.style.userSelect = 'none';
      }
    },

    // Sets all pages in the page list
    setPages(pages) {
      // Remove duplicates
      const ids = [];
      pages = pages.filter(page => {
        if (ids.includes(page.id)) {
          return false;
        } else {
          ids.push(page.id);
          return true;
        }
      });

      // Sort by isPinned, name
      pages = pages.sort((a, b) => {
        if (a.isPinned && !b.isPinned) {
          return -1;
        } else if (!a.isPinned && b.isPinned) {
          return 1;
        } else {
          return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
        }
      });

      // Set pages
      this.pages = pages;
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" :disable-scrolling="true" size="large">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Admin -->
      <sidebar-link-group v-if="$store.state.user.isAdmin" :title="$t('Admin')">
        <sidebar-link :text="$t('Enable Pages')" icon="el-icon-fa-check" @click="$refs.enablePagesDialog.open()" />
        <sidebar-link
          :text="$t('Manage Templates')"
          :to="{ name: 'Templates', params: { siteId: site.id } }"
          icon="el-icon-fa-code"
        />
        <sidebar-link
          :text="$t('Manage Blocks')"
          :to="{ name: 'Blocks', params: { siteId: site.id } }"
          icon="el-icon-fa-cube"
        />
        <sidebar-link :text="$t('Recent Activity')" icon="el-icon-fa-heart-rate" @click="$refs.activityDialog.open()" />
        <sidebar-link
          v-if="$store.state.user.canManageSites && !site.isDemo"
          :text="$t('Edit Site')"
          :to="{ name: 'EditSite', params: { siteId: site.id } }"
          icon="el-icon-fa-cog"
        />
      </sidebar-link-group>

      <!-- Actions -->
      <sidebar-link-group :title="$t('Actions')">
        <sidebar-link
          :disabled="site.isLocked || !hasTemplates"
          :text="$t('New Page')"
          :to="{ name: 'NewPage', params: { siteId: site.id } }"
          icon="el-icon-fa-plus"
        />
        <sidebar-link
          v-if="site.id && $store.state.user.account.gaAuthToken && site.gaProfileId"
          :disabled="site.isLocked || !['business', 'agency'].includes($store.state.user.account.plan)"
          :text="$t('Analytics')"
          :to="{ name: 'Analytics', params: { siteId: site.id } }"
          icon="el-icon-fa-chart-bar"
        />
        <sidebar-link :text="$t('Manage Files')" icon="el-icon-fa-folder" @click="openFileBrowser()" />
        <sidebar-link :text="$t('View Website')" :href="site.url" icon="el-icon-fa-external-link" />
      </sidebar-link-group>
    </template>

    <!-- Control bar -->
    <control-bar v-if="!site.isLocked" :collapsable="true">
      <template slot="primary">
        <el-input
          ref="search"
          v-model="search"
          :placeholder="$t('Search Pages')"
          :disabled="!hasPages"
          :prefix-icon="isSearching ? 'el-icon-fa-circle-notch el-icon-fa-spin' : 'el-icon-fa-search'"
          clearable
          @input="handleSearch"
        />
      </template>
      <template slot="secondary">
        <el-button-group>
          <el-button
            :disabled="selection.length !== 1"
            :title="$t('Edit')"
            @click="$router.push({ name: 'EditPage', params: { siteId: site.id, pageId: selection[0].id } })"
          >
            <i class="el-icon-fa-fw el-icon-fa-edit" />
          </el-button>
          <el-button
            :disabled="selection.length === 0"
            :title="$t('Pin to Top')"
            @click="pinPages(selection, !selection[0].isPinned)"
          >
            <i class="el-icon-fa-fw el-icon-fa-map-pin" />
          </el-button>
        </el-button-group>
        <el-button-group>
          <el-button
            :disabled="selection.length !== 1"
            :title="$t('Rename')"
            @click="$refs.renameDialog.open(selection[0])"
          >
            <i class="el-icon-fa-fw el-icon-fa-tag" />
          </el-button>
          <el-button
            v-if="site.canDuplicatePages"
            :disabled="selection.length !== 1"
            :title="$t('Duplicate')"
            @click="$refs.duplicateDialog.open(selection[0])"
          >
            <i class="el-icon-fa-fw el-icon-fa-copy" />
          </el-button>
          <el-button
            v-if="site.canDeletePages"
            :disabled="selection.length === 0"
            :title="$t('Delete')"
            @click="deletePage(selection, true)"
          >
            <i class="el-icon-fa-fw el-icon-fa-trash" />
          </el-button>
          <el-button
            v-if="$store.state.user.isAdmin"
            :disabled="selection.length === 0"
            :title="$t('Disable')"
            @click="deletePage(selection, false)"
          >
            <i class="el-icon-fa-fw el-icon-fa-file-times" />
          </el-button>
        </el-button-group>

        <el-button
          :disabled="!hasTemplates"
          type="primary"
          @click="$router.push({ name: 'NewPage', params: { siteId: site.id } })"
        >
          {{ $t('New Page') }}
        </el-button>
      </template>
    </control-bar>

    <!-- Pages -->
    <el-row v-show="!site.isLocked && hasPages" :gutter="20">
      <el-col :xs="24" :sm="24" :md="24" :lg="10" :xl="10">
        <!-- Page cards -->
        <content-box
          ref="pageCards"
          v-loading.body="isDeleting"
          class="page-cards"
          @click="$refs.selectable.selectNone()"
        >
          <selectable ref="selectable" v-model="selection" :items="pages" :multiple="true">
            <transition-group name="page-cards">
              <page-card
                v-for="page in pages"
                :key="page.id"
                :page="page"
                :selected="$refs.selectable && $refs.selectable.isSelected(page)"
                @click.stop="$refs.selectable.select(page, $event)"
                @dblclick.native="
                  $router.push({
                    name: 'EditPage',
                    params: {
                      siteId: site.id,
                      pageId: page.id
                    }
                  })
                "
              />
            </transition-group>
          </selectable>

          <!-- No results -->
          <div v-if="search.length && !pages.length" class="no-results">
            <div class="no-results-message">
              {{ $t('No Results') }}
            </div>
          </div>

          <!-- Load more button -->
          <div v-if="canLoadMore" class="text-center">
            <el-button :loading="isSearching" @click="loadMore">
              <i class="el-icon-fa-arrow-down" />
            </el-button>
          </div>
        </content-box>
      </el-col>
      <el-col :lg="14" :xl="14" class="hidden-md-and-down">
        <!-- Preview -->
        <content-box ref="pagePreview" class="page-preview">
          <!-- A preview is available -->
          <div v-show="isPreviewLoading || canShowPreview" v-loading="isPreviewLoading" class="preview-wrap-outer">
            <div class="preview-wrap-inner">
              <iframe
                v-show="!isPreviewLoading"
                ref="previewFrame"
                :src="iframeSrc"
                class="preview-frame"
                sandbox="allow-same-origin allow-scripts"
                tabindex="-1"
              />
            </div>
          </div>

          <!-- No preview available -->
          <div v-if="!canShowPreview" class="preview-count">
            <div class="preview-message">
              {{ $tc('{count} Pages Selected', selection.length, { count: selection.length }) }}
            </div>
          </div>
        </content-box>
      </el-col>
    </el-row>

    <!-- Empty state -->
    <empty-state
      v-show="!hasPages"
      :title="$t('Now that you\'re connected, let\'s enable some pages!')"
      class="empty-state"
    >
      <el-button type="primary" @click="$refs.enablePagesDialog.open()">
        {{ $t('Enable Pages') }}
      </el-button>
    </empty-state>

    <!-- Lockdown notice -->
    <div v-if="site.isLocked" class="lockdown">
      <div class="lockdown-body">
        <span class="icon"><i class="el-icon-fa-lock" /></span>
        <h2>{{ $t('This Site is on Lockdown') }}</h2>
        <small v-if="$store.state.user.isAdmin">
          {{ $t('Disable lockdown to make changes to this site.') }}
        </small>
        <small v-else>
          {{ $t('Please contact your administrator for details.') }}
        </small>
      </div>
    </div>

    <activity-dialog ref="activityDialog" :site="site" />
    <enable-pages-dialog ref="enablePagesDialog" :site="site" @page-enabled="handleEnabledPage" />
    <rename-dialog ref="renameDialog" :site="site" @complete="pageRenamed" />
    <duplicate-dialog ref="duplicateDialog" :site="site" @complete="pageDuplicated" />
    <el-dialog :visible.sync="fileBrowserDialogVisible" :title="$t('Manage Files')" class="manage-files-dialog">
      <file-browser
        ref="fileBrowser"
        :base-path="site.uploadFolder"
        :multiple="true"
        :show-pages="false"
        :show-select="false"
        :site="site"
        type="dialog"
      />
    </el-dialog>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.el-row {
  display: flex;
  flex: 1 1 auto;
  min-height: 0; // required for scrolling child flex elements with overflow: auto in Firefox
}

.page-cards {
  position: relative;
  min-height: 0;
  height: 100%;
  padding: 0;
  margin-bottom: 0;
  overflow: auto;
  transform: translateZ(0); // fix overflow + border-radius bug in Chrome

  @include breakpoint('sm-and-down') {
    padding: 0;
  }

  .el-button {
    margin: 1rem;
  }

  .page-card {
    transition: none;

    &.page-cards-move {
      transition: transform 0.25s, opacity 0.25s;
    }

    &.page-cards-move.selected {
      z-index: 2; // keep the selected item on top
    }

    // Only animate when items are being moved around (e.g. pinned)
    &.page-cards-enter,
    &.page-cards-enter-active {
      transition: none;
    }
  }
}

.page-preview {
  height: 100%;
  user-select: none;
  padding: 0;
  margin-bottom: 0;
}

.preview-none {
  font-size: 1.6rem;
  color: $--color-gray-40;
  padding: 2rem;
}

// Show page preview
.preview-wrap-outer {
  width: 100%;
  height: 100%;

  .preview-wrap-inner {
    position: relative;
    width: 200%;
    height: 200%;
    border-radius: 0.5rem;
    transform: scale(0.5);
    transform-origin: 0 0;
    overflow: hidden;
    transition: 0.1s box-shadow;

    &:hover {
      box-shadow: 0 0 0 0.4rem $--color-primary-light-7;
    }

    .preview-frame {
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 0.5rem;
      background: #fff;
      overflow-y: auto;
      display: block;
    }
  }
}

// No results
.no-results {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .no-results-message {
    font-size: 1.2rem;
    color: $--color-gray-40;
    padding: 2rem;
  }
}

// Show page count
.preview-count {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .preview-message {
    font-size: 1.2rem;
    color: $--color-gray-40;
    padding: 2rem;
  }
}

.lockdown {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;

  .lockdown-body {
    flex: 0 0 auto;
    color: $--color-gray-40;
    text-align: center;
    padding: 1rem;

    .icon {
      font-size: 6rem;
    }

    h2 {
      font-size: 1.6rem;
      color: inherit;
      margin-top: 1rem;
      margin-bottom: 1rem;
    }
  }
}
</style>
